import { createTheme, PaletteColorOptions, PaletteOptions } from '@mui/material';
import { createBreakpoints } from "@mui/system";
import { CompleteTheme } from "@nivo/core";

declare module '@mui/material/styles' {
    interface Palette {
        white: PaletteColorOptions;
        yellow: PaletteColorOptions;
        red: PaletteColorOptions;
    }
    interface PaletteOptions {
        white: PaletteColorOptions;
        yellow: PaletteColorOptions;
        red: PaletteColorOptions;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        white: true;
    }
}

declare module '@mui/material/IconButton' {
    interface IconButtonPropsColorOverrides {
        white: true;
    }
}

declare module '@mui/material/Fab' {
    interface FabPropsColorOverrides {
        white: true;
    }
}

const breakpoints = createBreakpoints({});

const SPACING = 8;

export const useSpacing = (value: number) => SPACING * value;

export const spacing = (value: number) => `${SPACING * value}px`;

const palette: PaletteOptions = {
    primary: {
        main: '#FF3399',
        dark: '#B2236B',
    },
    secondary: {
        main: '#336699',
    },
    success: {
        light: '#66CC88',
        main: '#5FB474',
        dark: '#228844',
    },
    warning: {
        light: '#ffb74d',
        main: '#ffa726',
        dark: '#f57c00',
    },
    error: {
        light: '#e57373',
        main: '#f44336',
        dark: '#d32f2f',
    },
    info: {
        main: '#55AAFF',
    },
    text: {
        secondary: "rgba(0, 0, 0, 0.9)",
    },
    white: {
        main: "#ffffff"
    },
    yellow: {
        light: "#ffd453",
        main: "#ffca28",
        dark: "#b28d1c",
    },
    red: {
        dark: "#a73a38",
        main: "#ef5350",
        light: "#f27573",
    },
    background: {
        default: "#EFEFEF",
        paper: "#FFFFFF",
    },
};

const theme = createTheme({
    palette: palette,
    typography: {
        fontFamily: [
            "CircularStd",
        ].join(','),
        h1: {
            fontWeight: 700,
            fontSize: '2rem',
            marginBottom: spacing(4),
            marginTop: spacing(4),
            [breakpoints.up('sm')]: { // not mobile
                fontSize: '2.5rem',
            },
        },
        h2: {
            fontWeight: 600,
            fontSize: '1.8rem',
            marginBottom: spacing(2),
            marginTop: spacing(2),
            [breakpoints.up('sm')]: { // not mobile
                fontSize: '2.5rem',
            },
            color: "#222222"
        },
        h3: {
            fontWeight: 600,
            fontSize: '1.5rem',
            marginBottom: spacing(2),
            marginTop: spacing(2),
            [breakpoints.up('sm')]: { // not mobile
                fontSize: '2rem',
            },
            color: "#222222"
        },
        h4: {
            fontWeight: 700,
            fontSize: '22px',
            marginBottom: spacing(2),
            marginTop: spacing(2),
            [breakpoints.up('sm')]: { // not mobile
                fontSize: '1.3rem',
            },
            color: "#333333"
        },
        h5: {
            fontWeight: 700,
            fontSize: "1rem",
            lineHeight: "150%"
        },
    },
    shape: {
        borderRadius: 4,
    },
    spacing: spacing,
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                "@global": {
                    html: {
                        [breakpoints.down('sm')]: {
                            fontSize: 13,
                        }
                    }
                }
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    paddingTop: spacing(2),
                    paddingBottom: spacing(4),
                }
            },
        },
        MuiTypography: {
            styleOverrides: {
                h5: {
                    color: "#333333",
                }
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    textTransform: 'none',

                    "& .MuiLoadingButton-loadingIndicator .MuiCircularProgress-root": {
                        position: "relative"
                    }
                },
                startIcon: {
                    "& svg": {
                        fontSize: "1rem",
                    }
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                }
            }
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    paddingBottom: spacing(2),
                }
            }
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    marginLeft: spacing(1),
                },
                indicator: {
                    height: 3,
                    borderTopLeftRadius: 3,
                    borderTopRightRadius: 3,
                    backgroundColor: "white",
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    margin: '0 16px',
                    minWidth: 0,
                    padding: 0,
                    [breakpoints.up('md')]: {
                        padding: 0,
                        minWidth: 0,
                    },
                },
            }
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    padding: spacing(1),
                },
            },
            variants: [
                {
                    props: { color: "white" },
                    style: {
                        "& svg": {
                            color: '#ffffff',
                        },
                    },
                }
            ]
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    borderRadius: 4,
                },
            },
        },
        MuiSpeedDialAction: {
            styleOverrides: {
                staticTooltipLabel: {
                    whiteSpace: "nowrap",
                }
            }
        },
        MuiDivider: {
            defaultProps: {
                light: true,
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    textDecoration: "none",
                    textDecorationStyle: "unset",
                }
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    // fontWeight: 500,
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                    marginRight: 0,
                    '& svg': {
                        fontSize: 20,
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: "rgba(0,0,0,0.6)",
                }
            }
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    width: spacing(4),
                    height: spacing(4),
                },
            }
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    width: "100%",
                }
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    backgroundColor: "white",
                }
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    width: "100%",
                }
            },
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    position: "absolute",
                }
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    paddingTop: spacing(2),
                }
            }
        },
        MuiDialogTitle: {
            defaultProps: {
                variant: "h5",
            },
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    paddingBottom: spacing(3),
                    paddingRight: spacing(6),
                    paddingLeft: spacing(6),
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    paddingBottom: spacing(4),
                    paddingRight: spacing(6),
                    paddingLeft: spacing(6),
                }
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    paddingBottom: spacing(4),
                    paddingRight: spacing(6),
                    paddingLeft: spacing(6),

                    "& button": {
                        fontSize: "1rem",
                    }
                }
            },
        },
    },
    mixins: {
        toolbar: {
            minHeight: 48,
        },
    },
});

export const PIE_CHART_THEME: Partial<CompleteTheme> = {
    labels: {
        text: {
            fontWeight: "bold",
        }
    }
};

export const Z_INDEX_BOTTOM_NAV_BAR = 1205;

export default theme;